// Every model call site, with what would actually go on the wire right now.
// This doubles as the model-config view: the effective model is a column,
// and changing it is a new version on the touchpoint behind the row.
import { SlidersHorizontal } from "lucide-react";
import { useNavigate } from "react-router";
import { DataTable } from "@/components/gb/data-table";
import { EmptyState } from "@/components/gb/empty-state";
import { PageHeader } from "@/components/gb/page-header";
import { useTouchpoints } from "./prompts-api";

export function PromptsPage() {
  const touchpoints = useTouchpoints();
  const navigate = useNavigate();

  if (touchpoints.isError) {
    return (
      <EmptyState
        icon={SlidersHorizontal}
        title="Couldn’t load touchpoints"
        description={String(touchpoints.error)}
      />
    );
  }

  return (
    <div>
      <PageHeader eyebrow="Platform" title="Prompts & models" />
      <p className="mt-1 max-w-[640px] text-[0.8rem] text-muted">
        One row per model call. Version 0 is the code default shipped with this
        build; anything above it is an admin override, and reverting is one
        click on the touchpoint.
      </p>
      <div className="mt-6">
        <DataTable
          columns={[
            {
              key: "name",
              header: "Touchpoint",
              render: (t) => (
                <div className="min-w-0">
                  <div className="truncate text-[0.85rem] font-semibold text-ink">{t.name}</div>
                  <div className="truncate font-mono text-[0.7rem] text-faint">{t.slug}</div>
                </div>
              ),
            },
            {
              key: "area",
              header: "Area",
              width: 100,
              render: (t) => <span className="font-mono text-[0.72rem] text-muted">{t.area}</span>,
            },
            {
              key: "provider",
              header: "Provider",
              width: 90,
              render: (t) => (
                <span className="font-mono text-[0.72rem] text-muted">{t.provider}</span>
              ),
            },
            {
              key: "model",
              header: "Effective model",
              width: 220,
              render: (t) => (
                <span className="truncate font-mono text-[0.72rem] text-ink">
                  {t.effectiveModel}
                </span>
              ),
            },
            {
              key: "version",
              header: "Live version",
              width: 110,
              render: (t) => (
                <span className="font-mono text-[0.75rem] text-muted">
                  v{t.activeVersion}
                  {t.activeVersion === 0 ? " (code)" : ""}
                  {t.outOfDateVersion !== null && (
                    <span className="text-warn"> · v{t.outOfDateVersion} out of date</span>
                  )}
                </span>
              ),
            },
          ]}
          rows={touchpoints.data ?? []}
          getRowKey={(t) => t.slug}
          onSelect={(t) => void navigate(`/prompts/${t.slug}`)}
          emptyMessage="No touchpoints."
        />
      </div>
    </div>
  );
}
